/**
 * Shared builders for GET /v1/search sample URLs and copy-paste snippets.
 *
 * Landing page and SearchPlayground both use this so the curl/JS samples stay
 * aligned with the public query shape in docs and the live try path.
 */

import {
    DEFAULT_ITEM_LOCATION,
    type EbayCountryCode,
    type ItemLocationCode,
} from '../data/countries';

export type SampleSearchForm = {
    keyword: string;
    sold: boolean;
    country: EbayCountryCode;
    itemLocation: ItemLocationCode;
    page: string;
    count: string;
    daysToScrape: string;
    sort: string;
    condition: string;
    listingType: string;
    minPrice: string;
    maxPrice: string;
};

export type BuildSearchParamsOptions = {
    /**
     * Emit every form-controlled field, including API defaults.
     * Used by the docs try panel so changing a select always rewrites the snippet.
     * Minimal mode (default) omits defaults, cleaner landing-page curl.
     */
    explicit?: boolean;
};

function parseOptionalNumber(raw: string): number | undefined {
    const t = raw.trim();
    if (!t) return undefined;
    const n = Number(t);
    return Number.isFinite(n) ? n : undefined;
}

function clampInt(raw: string, min: number, max: number, fallback: number): number {
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
}

/** Matches `EbaySearchQuerySchema.count` min. */
export const COUNT_MIN = 1;
/** Default on blank / non-numeric input. */
export const COUNT_DEFAULT = 60;
/**
 * Public GET /v1/search schema max. Docs say ~200 from eBay; schema stays 240
 * deliberately (see EbaySearchQuerySchema).
 */
export const PUBLIC_COUNT_MAX = 240;
/** POST /api/console/search body max, try panel sample, not full scan. */
export const CONSOLE_COUNT_MAX = 60;

/** Whole number only; null when empty or not a finite integer. */
export function parseCountInput(raw: string): number | null {
    const t = raw.trim();
    if (!t) return null;
    // Reject floats / junk ("12.5", "1e3"), API requires int.
    if (!/^-?\d+$/.test(t)) return null;
    const n = Number(t);
    if (!Number.isSafeInteger(n)) return null;
    return n;
}

/** Clamp into a valid count range. */
export function clampCount(n: number, max: number, min = COUNT_MIN): number {
    return Math.min(max, Math.max(min, n));
}

/**
 * Resolve count for a call path. User may type anything; this is where limits
 * apply, public snippets (≤240) vs console try (≤60).
 */
export function resolveCount(
    raw: string,
    max: number,
    fallback: number = COUNT_DEFAULT,
): number {
    const parsed = parseCountInput(raw);
    if (parsed === null) return clampCount(fallback, max);
    return clampCount(parsed, max);
}

/** @deprecated use resolveCount(form.count, PUBLIC_COUNT_MAX) */
export function resolveSnippetCount(form: SampleSearchForm, max = PUBLIC_COUNT_MAX): number {
    return resolveCount(form.count, max, COUNT_DEFAULT);
}

/** @deprecated use resolveCount(form.count, CONSOLE_COUNT_MAX) */
export function resolveTryCount(form: SampleSearchForm, max = CONSOLE_COUNT_MAX): number {
    return resolveCount(form.count, max, COUNT_DEFAULT);
}

export type CountFieldState = {
    /** Value that will actually be sent after limits. */
    resolved: number;
    /** True when the raw string is not a legal /v1 count as typed. */
    invalid: boolean;
    /** Short line under the field. */
    hint: string;
};

/**
 * Field UI for free typing + visible limits. Snippets use PUBLIC_COUNT_MAX;
 * try clamps separately with CONSOLE_COUNT_MAX.
 */
export function countFieldState(raw: string, max: number = PUBLIC_COUNT_MAX): CountFieldState {
    const parsed = parseCountInput(raw);
    if (parsed === null) {
        if (raw.trim() === '') {
            return {
                resolved: clampCount(COUNT_DEFAULT, max),
                invalid: false,
                hint: `1–${max}`,
            };
        }
        return {
            resolved: clampCount(COUNT_DEFAULT, max),
            invalid: true,
            hint: `Whole number, 1–${max}`,
        };
    }
    if (parsed < COUNT_MIN) {
        return {
            resolved: COUNT_MIN,
            invalid: true,
            hint: `Min ${COUNT_MIN}, will use ${COUNT_MIN}`,
        };
    }
    if (parsed > max) {
        return {
            resolved: max,
            invalid: true,
            hint: `Max ${max}, will use ${max}`,
        };
    }
    return {
        resolved: parsed,
        invalid: false,
        hint: `1–${max}`,
    };
}

/** Normalize raw count text after blur (or before submit). */
export function normalizeCountInput(raw: string, max: number): string {
    return String(resolveCount(raw, max));
}

/**
 * Build the query string for GET /v1/search from a form-shaped object.
 *
 * `count` is passed separately so the try path can clamp (≤60) while the
 * public snippet uses the user-entered ceiling (≤240).
 */
export function buildV1SearchParams(
    form: SampleSearchForm,
    count: number,
    options: BuildSearchParamsOptions = {},
): URLSearchParams {
    const explicit = options.explicit === true;
    const params = new URLSearchParams();

    params.set('keyword', form.keyword.trim() || 'iphone 15 pro');
    params.set('sold', form.sold ? 'true' : 'false');
    params.set('count', String(count));
    params.set('country', form.country);

    if (explicit || form.itemLocation !== DEFAULT_ITEM_LOCATION) {
        params.set('itemLocation', form.itemLocation);
    }

    const page = clampInt(form.page, 1, 100, 1);
    if (explicit || page > 1) params.set('page', String(page));

    const days = clampInt(form.daysToScrape, 0, 365, 0);
    if (explicit || days > 0) params.set('daysToScrape', String(days));

    if (explicit || form.sort !== 'newly_listed') params.set('sort', form.sort);
    if (explicit || form.listingType !== 'all') params.set('listingType', form.listingType);

    const condition = form.condition.trim();
    if (condition) params.set('condition', condition);

    const minPrice = parseOptionalNumber(form.minPrice);
    const maxPrice = parseOptionalNumber(form.maxPrice);
    if (minPrice !== undefined) params.set('minPrice', String(minPrice));
    if (maxPrice !== undefined) params.set('maxPrice', String(maxPrice));

    return params;
}

export function buildV1SearchUrl(
    origin: string,
    form: SampleSearchForm,
    count: number,
    options: BuildSearchParamsOptions = {},
): string {
    return `${origin}/v1/search?${buildV1SearchParams(form, count, options).toString()}`;
}

/** Shell-safe single-quoted value for curl --data-urlencode. */
function shellSingleQuote(value: string): string {
    return `'${value.replace(/'/g, `'\\''`)}'`;
}

/** Placeholder when the real secret is unknown or the reader is not signed in. */
export const SAMPLE_API_KEY_PLACEHOLDER = 'as_live_YOUR_KEY';

export type SnippetAuthOptions = BuildSearchParamsOptions & {
    /**
     * Full `as_live_…` secret for a signed-in console session. Omit (or leave
     * blank) and snippets keep the public placeholder. Never persist this.
     */
    apiKey?: string;
};

function resolveSnippetApiKey(apiKey: string | undefined): string {
    const trimmed = apiKey?.trim();
    return trimmed ? trimmed : SAMPLE_API_KEY_PLACEHOLDER;
}

/**
 * Multi-line curl so each query param is a visible line (no horizontal scroll soup).
 * `-G` + `--data-urlencode` is the correct way to attach a GET query string.
 */
export function formatV1SearchCurl(
    origin: string,
    form: SampleSearchForm,
    count: number,
    options: SnippetAuthOptions = {},
): string {
    const params = buildV1SearchParams(form, count, options);
    const bearer = resolveSnippetApiKey(options.apiKey);
    const lines = [`curl -sS -G ${shellSingleQuote(`${origin}/v1/search`)} \\`];
    for (const [key, value] of params.entries()) {
        lines.push(`  --data-urlencode ${shellSingleQuote(`${key}=${value}`)} \\`);
    }
    lines.push(`  -H ${shellSingleQuote(`Authorization: Bearer ${bearer}`)}`);
    return lines.join('\n');
}

/**
 * Multi-line fetch sample. Uses URLSearchParams so every param is editable in place.
 */
export function formatV1SearchJs(
    origin: string,
    form: SampleSearchForm,
    count: number,
    options: SnippetAuthOptions = {},
): string {
    const params = buildV1SearchParams(form, count, options);
    const entries = [...params.entries()];
    const body = entries.map(([key, value]) => `  ${JSON.stringify(key)}: ${JSON.stringify(value)},`).join('\n');
    const bearer = resolveSnippetApiKey(options.apiKey);

    return [
        `const qs = new URLSearchParams({`,
        body,
        `});`,
        ``,
        `const res = await fetch(\`${origin}/v1/search?\${qs}\`, {`,
        `  headers: { Authorization: 'Bearer ${bearer}' },`,
        `});`,
        `const data = await res.json();`,
        `console.log(data.totalItems, data.items);`,
    ].join('\n');
}

/** Landing-page default: sold comps, US, sixty rows. */
export const LANDING_SAMPLE_FORM: SampleSearchForm = {
    keyword: 'baseball caps',
    sold: true,
    country: 'US',
    itemLocation: 'worldwide',
    page: '1',
    count: '60',
    daysToScrape: '0',
    sort: 'newly_listed',
    condition: '',
    listingType: 'all',
    minPrice: '',
    maxPrice: '',
};
